import { Button, useTranslation } from '@hbs-us/ui';
import { type FormEvent, useId, useState } from 'react';

import { RichTextEditor } from '../../../components/RichTextEditorLazy.js';
import { downloadCredentialed } from '../../../lib/download.js';
import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { JobDto } from '../contractsMirror.js';
import { changeOrderPdfUrl } from '../exportLinks.js';
import {
  type ChangeOrder,
  useApproveChangeOrderMutation,
  useCreateChangeOrderMutation,
  useJobChangeOrdersQuery,
} from '../useJobQuery.js';

/**
 * Change orders on a job — extra work agreed after the job started, raising its contract.
 *
 * They live here and nowhere else. A change order is stored as an estimate row (it is structurally
 * one: line items, totals, a document the customer can sign), but it is NOT prospective work, so
 * showing it in the estimates pipeline would inflate pipeline value with work already under
 * contract. A lint gate on the server holds every estimates query to that.
 *
 * ⚠ "Approved" here means the CONTRACT HAS MOVED, not that somebody clicked something. The two are
 * tracked separately on the server — status is the customer's answer, `appliedAt` is the money —
 * and this panel shows the money, because that is the question somebody looking at a contract has.
 */
export function ChangeOrdersPanel({ job }: { job: JobDto }) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const query = useJobChangeOrdersQuery(job.id);
  const approve = useApproveChangeOrderMutation(job.id);
  const [confirming, setConfirming] = useState<ChangeOrder | null>(null);

  const changeOrders = query.data?.changeOrders ?? [];
  const contractAmount = query.data?.contractAmount ?? Number(job.contractAmount ?? 0);
  const appliedTotal = changeOrders
    .filter((co) => co.appliedAt)
    .reduce((sum, co) => sum + co.total, 0);

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-foreground text-lg">{t('jobs:changeOrders.heading')}</h3>
        <p className="text-muted-foreground text-sm">
          {t('jobs:changeOrders.contractNow', { amount: fmt.formatCurrency(contractAmount) })}
          {appliedTotal !== 0 ? (
            <>
              {' · '}
              {t('jobs:changeOrders.fromChangeOrders', {
                amount: fmt.formatCurrency(appliedTotal),
              })}
            </>
          ) : null}
        </p>
      </div>

      {changeOrders.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('jobs:changeOrders.none')}</p>
      ) : (
        <ul className="mb-4 space-y-2">
          {changeOrders.map((co) => (
            <li
              key={co.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground text-sm">
                  <span className="font-mono">{co.number}</span>
                  {co.title ? ` — ${co.title}` : null}
                </p>
                <p className="text-muted-foreground text-xs">
                  {co.appliedAt
                    ? t('jobs:changeOrders.applied')
                    : t('jobs:changeOrders.notApplied')}
                  {co.signerName
                    ? ` · ${t('jobs:changeOrders.signedBy', { name: co.signerName })}`
                    : null}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {/* A deductive change order reads as a credit, so the sign is never ambiguous. */}
                <span
                  className={
                    co.total < 0
                      ? 'font-mono text-destructive text-sm'
                      : 'font-mono text-foreground text-sm'
                  }
                >
                  {co.total < 0 ? '−' : '+'}
                  {fmt.formatCurrency(Math.abs(co.total))}
                </span>
                {/* Downloadable whatever its state — an unapproved change order is exactly the
                    document you send a customer to get it approved. Credentialed GET through the
                    api host, the same path the estimate PDF uses. */}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    void downloadCredentialed(
                      changeOrderPdfUrl(co.id),
                      `change-order-${co.number || co.id}.pdf`,
                    ).catch((err) => console.error('change order pdf download failed', err))
                  }
                >
                  {t('jobs:changeOrders.download')}
                </Button>
                {co.appliedAt ? null : (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={approve.isPending}
                    onClick={() => setConfirming(co)}
                  >
                    {t('jobs:changeOrders.approve')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {approve.isError ? (
        <p role="alert" className="mb-3 text-destructive text-sm">
          {t('jobs:changeOrders.approveError')}
        </p>
      ) : null}

      <NewChangeOrderForm jobId={job.id} />

      <ConfirmDialog
        open={confirming !== null}
        heading={t('jobs:changeOrders.confirmHeading')}
        body={
          confirming
            ? t('jobs:changeOrders.confirmBody', {
                amount: fmt.formatCurrency(confirming.total),
                newTotal: fmt.formatCurrency(contractAmount + confirming.total),
              })
            : ''
        }
        confirmLabel={t('jobs:changeOrders.confirmAction')}
        cancelLabel={t('jobs:changeOrders.confirmCancel')}
        confirmPending={approve.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          const target = confirming;
          setConfirming(null);
          if (target) approve.mutate({ changeOrderId: target.id });
        }}
      />
    </section>
  );
}

/**
 * Raising a change order.
 *
 * A NEGATIVE amount is allowed and labelled as such: a deductive change order covers work removed,
 * and it is how a change order the customer has already signed gets corrected — by issuing its
 * opposite rather than editing a signed document. Zero is refused by the server.
 */
function NewChangeOrderForm({ jobId }: { jobId: number }) {
  const { t } = useTranslation();
  const create = useCreateChangeOrderMutation(jobId);
  const titleId = useId();
  const totalId = useId();
  const scopeId = useId();
  const [title, setTitle] = useState('');
  const [total, setTotal] = useState('');
  const [scope, setScope] = useState('');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(
      { title: title || undefined, scopeOfWork: scope || undefined, total: Number(total) },
      {
        onSuccess: () => {
          setTitle('');
          setTotal('');
          setScope('');
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="border-border border-t pt-4">
      <h4 className="mb-3 font-medium text-foreground text-sm">
        {t('jobs:changeOrders.newHeading')}
      </h4>
      {create.isError ? (
        <p role="alert" className="mb-3 text-destructive text-sm">
          {t('jobs:changeOrders.createError')}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={titleId} className="font-medium text-foreground text-sm">
            {t('jobs:changeOrders.title')}
          </label>
          <input
            id={titleId}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={INPUT_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={totalId} className="font-medium text-foreground text-sm">
            {t('jobs:changeOrders.amount')}
          </label>
          <input
            id={totalId}
            type="number"
            step="0.01"
            required
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            className={INPUT_CLASS}
          />
          <p className="text-muted-foreground text-xs">{t('jobs:changeOrders.amountHint')}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor={scopeId} className="font-medium text-foreground text-sm">
          {t('jobs:changeOrders.scope')}
        </label>
        {/* Same editor as the estimate and invoice builders, and for the same reason: this lands in
            scope_of_work, which stores HTML and is rendered — not escaped — onto the customer's
            document. The lazy wrapper keeps TipTap out of the entry chunk, so only opening this tab
            pays for it. */}
        <RichTextEditor id={scopeId} value={scope} onChange={setScope} />
      </div>
      <div className="mt-4">
        <Button type="submit" disabled={create.isPending}>
          {t('jobs:changeOrders.create')}
        </Button>
      </div>
    </form>
  );
}

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
